import moment from "moment";
import { useCallback, useEffect, useState } from 'react';
import { HiOutlineEye, HiOutlinePencil } from "react-icons/hi2";
import { ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import MaintenanceDetailModal from '../../../components/forms/MaintenanceDetailModal';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import Page from '../../../layout/Page/Page';
import { MaintenanceService } from '../../../services/maintenance/maintenanceService';
import { MaintenancesApiResponse } from '../../../type/entities/maintenance-type';
import { maintenanceTypes } from "../../../utils/SelectorMapping";
import MaintenanceForm from '../components/MaintenanceForm';
import MaintenanceFilters from './MaintenanceFilters';
import MaintenanceDeleteAllModal from "../components/MaintenanceDeleteAllModal";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useHandleErrors from "../../../hooks/useHandleErrors";

interface MaintenancesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    maintenanceSelected: any;
    setMaintenanceSelected: React.Dispatch<React.SetStateAction<any>>;
    openDeleteAllModal: boolean;
    setOpenDeleteAllModal: React.Dispatch<React.SetStateAction<boolean>>;
    registerTableRefetch?: (refetch: () => void) => void;
}

const columnOrder = [
    'id',
    'vehicle',
    'type',
    'date',
    'cost',
    'odometerKm',
    'description',
];

const MaintenancesList: React.FC<MaintenancesListProps> = ({ openFormDrawer, setOpenFormDrawer, maintenanceSelected, setMaintenanceSelected, openDeleteAllModal, setOpenDeleteAllModal, registerTableRefetch }) => {

    // STATES

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [selectedMaintenance, setSelectedMaintenance] = useState<any>(null);
    const [detailModalOpen, setDetailModalOpen] = useState(false);

    const service = new MaintenanceService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTINE EL LISTADO DE MANTENIMIENTOS
     * @EN OBTAINS THE LIST OF MAINTENANCES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listMaintenances(filters);
        return response.getResponseData() as MaintenancesApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        registerTableRefetch?.(refetch);
    }, [registerTableRefetch, refetch]);

    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA UN MANTENIMIENTO
     * @EN DELETES A MAINTENANCE
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.deleteMaintenance(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Mantenimiento eliminado correctamente');
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINA MULTIPLES MANTENIMIENTOS
     * @EN DELETES MULTIPLE MAINTENANCES
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDeleteMultiple = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiMaintenances(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Mantenimientos eliminados correctamente');
                refetch();
            } else {
                toast.error('Error al eliminar los mantenimientos');
            }
        } catch (error) {
            toast.error('Error al eliminar los mantenimientos');
        }
    };


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES ORDENA LOS DATOS SEGUN EL ORDEN DE COLUMNAS
     * @EN SORTS THE DATA ACCORDING TO THE COLUMN ORDER
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------

    
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA UN NUMERO EN FORMATO MONEDA
     * @EN FORMATS A NUMBER INTO CURRENCY FORMAT
     */
    // -------------------------------------------------------------------------------------------------------------
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };
    // -------------------------------------------------------------------------------------------------------------

    // RENDER
    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'maintenances-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'vehicle',
                            columnNameKey: 'Vehículo',
                            render: (row: any) => {
                                const vehicle = row.row.original.vehicle;
                                const isSold = vehicle?.status === 'sold';
                                return (
                                    <div className="flex flex-col">
                                        <span className={isSold ? 'text-red-600 font-medium' : ''}>
                                            {vehicle?.plateNumber || 'Sin matrícula'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {vehicle?.brand} {vehicle?.model}
                                        </span>
                                        {isSold && (
                                            <span className="text-xs text-red-500 font-medium">
                                                (Vendido)
                                            </span>
                                        )}
                                    </div>
                                );
                            }
                        },
                        {
                            key: 'type',
                            columnNameKey: 'Tipo de intervención',
                            render: (row: any) => {
                                const type = row.row.original.type;
                                return maintenanceTypes.find((type) => type.value === row.row.original.type)?.label || 'Sin tipo';
                            }
                        },
                        {
                            key: 'company',
                            columnNameKey: 'Compañía',
                            permissions: [{
                                group: 'company',
                                permission: 'list'
                            }],
                            render: (row: any) => {
                                const company = row.row.original.company;
                                return company ? company.name : 'N/A';
                            }
                        },
                        {
                            key: 'date',
                            columnNameKey: 'Fecha',
                            render: (row: any) => {
                                const date = row.row.original?.date?.date || row.row.original?.date;
                                return date ? moment(date).format('DD-MM-YYYY') : 'Sin Fecha';
                            },
                        },
                        {
                            key: 'cost',
                            columnNameKey: 'Coste',
                            render: (row: any) => {
                                const cost = row.row.original.cost;
                                return cost ? formatCurrency(parseFloat(cost)) : 'Sin coste';
                            },
                        },
                        {
                            key: 'odometerKm',
                            columnNameKey: 'Km',
                            render: (row: any) => {
                                const km = row.row.original.odometerKm;
                                if (km === null || km === undefined || km === '') return '—';
                                return `${Number(km).toLocaleString('es-ES')} km`;
                            },
                        },
                        {
                            key: 'description',
                            columnNameKey: 'Descripción',
                            render: (row: any) => {
                                const description = row.row.original.description;
                                if (!description) return 'Sin descripción';
                                return description.length > 50
                                    ? `${description.substring(0, 50)}...`
                                    : description;
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'maintenance', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver detalle'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setSelectedMaintenance(row);
                                                setDetailModalOpen(true);
                                            }}
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer"
                                        >
                                            <HiOutlineEye className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'maintenance', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setMaintenanceSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer"
                                        >
                                            <HiOutlinePencil className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'maintenance', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar`}
                                                description={`¿Estás seguro de que deseas eliminar este mantenimiento?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleDeleteMultiple}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as MaintenancesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Mantenimientos'} onClear={() => resetFilters(50)}>
                <MaintenanceFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <MaintenanceDetailModal
                isOpen={detailModalOpen}
                onClose={() => {
                    setDetailModalOpen(false);
                    setSelectedMaintenance(null);
                }}
                maintenance={selectedMaintenance}
            />

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                drawerTitle={maintenanceSelected ? 'Editar Mantenimiento' : 'Crear Mantenimiento'}
                subtitleText={
                    maintenanceSelected ?
                        "Completa el formulario para editar los datos del mantenimiento" :
                        "Completa el formulario para crear un nuevo mantenimiento en el sistema"
                }
                submitText={maintenanceSelected ? 'Guardar Mantenimiento' : 'Crear Mantenimiento'}
            >
                <MaintenanceForm setOpenModal={setOpenFormDrawer} refetch={refetch} maintenance={maintenanceSelected} />
            </FormDrawerView>

            <MaintenanceDeleteAllModal
                isOpen={openDeleteAllModal}
                onClose={() => setOpenDeleteAllModal(false)}
                refetch={refetch}
            />
        </Page>
    );
};

export default MaintenancesList;